import { Box, Button, Group, Stack, Text, VisuallyHidden } from "@mantine/core";
import { FC, useState } from "react";
import { ReactComponent as RightIcon } from "assets/expand.svg";
import { ReactComponent as PalleteIcon } from "assets/Palette.svg";
import FilterModal from "./FilterModal";
import {
  DATA_SOURCE_LABEL_MAP,
  DEFAULT_PREVIOUS_SALES_DRAFT,
  DEFAULT_PREVIOUS_SALES_STATE,
  migratePreviousSales,
  PREVIOUS_SALES_ALL,
  PreviousSalesDraft,
  PreviousSalesState,
  PREVIOUS_SALES_NO_MATCHES_SUMMARY,
  previousSalesState,
  previousSalesSummaryParts,
  SPECIALTY_COLOR_MAP,
  STATUS_COLOR_MAP,
  TYPE_TITLE_MAP
} from "../consts";
import { ColorType } from "../ColorsPanel";

interface Props {
  onApplyFilter: (f: FilterControl) => void;
  initFilterControl?: FilterControl;
  colorControl?: ColorType;
}

// EIS-1097: "rolling-12m-sales" is GONE. Its dollar range is now the Has
// segment of "previous-sales", so a separate row (and a separate FilterType
// member that TYPE_TITLE_MAP / FILTER_CONTENT_MAP would have to answer for)
// would be a second way to ask one question.
export type FilterType = "last-check-in" | "status" | "until-follow-up"
  | "specialty-type" | "source" | "previous-sales";

export const END_CHECK_IN_SCALE = 2724;

export interface FilterControl {
  minDaysSinceLastCheckIn: number | null;
  maxDaysSinceLastCheckIn: number | null;
  isCheckInFilterActive: boolean;
  minDaysUntilFollowUp: number | null;
  maxDaysUntilFollowUp: number | null;
  status: string[] | null;
  specialtyType: string[] | null;
  // EIS-1088
  dataSource: string[] | null;
  // EIS-1097: the MERGED Previous Sales filter -- a DISCRIMINATED UNION carrying
  // the segment, the window, and (under Has only) the dollar range. EIS-1091's
  // bare scalar and EIS-1093's three sibling rolling-12m keys are both gone; see
  // PreviousSalesState in ../consts for why None cannot carry a range and why
  // there is no arming flag any more (the segment IS the arming).
  previousSales: PreviousSalesState;
  // The editor's unshown values, so switching segments does not discard what the
  // user typed (AC2). INERT: never read by buildMapFilterBody or isFilterActive.
  previousSalesDraft: PreviousSalesDraft;
}

export interface FilterComponentProps {
  onFilterControlChange: (fc: Partial<FilterControl>) => void;
  filterControl: Partial<FilterControl>;
}

export const DEFAULT_VALUES: FilterControl = {
  minDaysSinceLastCheckIn: 0,
  maxDaysSinceLastCheckIn: END_CHECK_IN_SCALE,
  isCheckInFilterActive: false,
  minDaysUntilFollowUp: null,
  maxDaysUntilFollowUp: null,
  status: Array.from(STATUS_COLOR_MAP.keys()),
  specialtyType: Array.from(SPECIALTY_COLOR_MAP.keys()),
  // EIS-1088: every source toggle is ON by default (= inactive)
  dataSource: Array.from(DATA_SOURCE_LABEL_MAP.keys()),
  // EIS-1091/1097: Previous Sales defaults to "All" -- the identity selection,
  // which reveals no controls, sends no wire key, and leaves the badge off.
  previousSales: DEFAULT_PREVIOUS_SALES_STATE,
  // "between $1 to no limit, in the last 12 months" -- what the Has segment shows
  // the first time it is opened. $1 is a DEFAULT, not a clamp: the Has segment
  // already asserts sales > 0, so (unlike EIS-1093's standalone range) a $0
  // minimum here cannot sweep in accounts that never bought anything.
  previousSalesDraft: DEFAULT_PREVIOUS_SALES_DRAFT,
}

/**
 * EIS-1088: normalize a persisted `map-filters` blob.
 *
 * A blob written before EIS-1088 has no `dataSource`/`previousSales` keys (and a
 * blob written by a "Toggle All Off"-then-reload path could carry null), which
 * must degrade to "all toggles on" -- i.e. the filter is INACTIVE -- and never to
 * an empty selection (which would render an empty map).
 *
 * EIS-1097 (AC11) makes `previousSales` a MIGRATION across THREE shipped shapes,
 * not a default. `??` is not enough for any of them: it does not catch EIS-1088's
 * `[]` (which legitimately meant "show nothing"), it cannot turn EIS-1091's bare
 * scalar into the union, and it cannot find EIS-1093's dollar range, which lived
 * in three SIBLING keys (minRolling12mSales / maxRolling12mSales /
 * isRolling12mFilterActive) that no longer exist. migratePreviousSales() folds
 * all of them onto (union, draft) -- see ../consts for the full table -- always
 * erring towards All, which removes no pins.
 *
 * `fc` is typed but is in reality whatever JSON.parse handed back, hence the
 * cast to unknown at the boundary.
 *
 * Callers must memoize the result: MapComponent re-fetches pins on
 * `filterControl` identity change.
 */
/** The EIS-1093 keys the merged row absorbed. Spreading a legacy blob would
 *  otherwise carry them straight back into localStorage on the next write --
 *  harmless today, but they would sit there looking authoritative next to the
 *  union that actually holds the range. migratePreviousSales() has already read
 *  them by the time they are dropped. */
const RETIRED_ROLLING_12M_KEYS = [
  "minRolling12mSales", "maxRolling12mSales", "isRolling12mFilterActive",
] as const;

export const withFilterDefaults = (fc?: FilterControl): FilterControl => {
  const carried = { ...(fc || {}) } as Record<string, unknown>;
  RETIRED_ROLLING_12M_KEYS.forEach((key) => { delete carried[key]; });
  return {
    ...DEFAULT_VALUES,
    ...(carried as Partial<FilterControl>),
    dataSource: fc?.dataSource ?? DEFAULT_VALUES.dataSource,
    ...migratePreviousSales(fc as unknown),
  };
};

export const isFilterActive = (fc?: FilterControl): boolean => (
  Object.entries(fc || {}).some(([k, v]) => {
    if (k === "isCheckInFilterActive") {
      if (v) {
        return true
      }
      return false;
    }
    if (k === "status") {
      if (v.length !== STATUS_COLOR_MAP.size) {
        return true;
      }
      return false;
    }
    if (k === "specialtyType") {
      if (v.length !== SPECIALTY_COLOR_MAP.size) {
        return true;
      }
      return false;
    }
    // EIS-1088: a missing key never reaches here (Object.entries skips it), so a
    // stale blob reads as inactive; a null value also reads as inactive, which
    // matches withFilterDefaults() coercing null back to "all on".
    if (k === "dataSource") {
      return !!v && v.length !== DATA_SOURCE_LABEL_MAP.size;
    }
    // EIS-1091/1097: All is the only inactive segment, and the SEGMENT is the
    // whole story -- there is no separate arming flag to disagree with it. Note
    // this reads the RAW value through the same migration used everywhere else,
    // so an unnormalized legacy blob (a bare "has", an EIS-1088 array, `[]`,
    // null or junk) lights the badge exactly when the migrated state would --
    // the badge cannot disagree with the wire body.
    if (k === "previousSales") {
      return previousSalesState(v).mode !== PREVIOUS_SALES_ALL;
    }
    // Every other key -- including previousSalesDraft, which is editor scratch
    // space and must NEVER light the badge -- is inactive by construction.
    return false;
  })
);

const FiltersPanel: FC<Props> = ({
  onApplyFilter,
  initFilterControl,
  colorControl
}) => {
  // EIS-1088: normalize defensively -- a stale persisted blob must never make the
  // new Source / Previous Sales editors render with every toggle off.
  const [filterControl, setFilterControl] = useState<FilterControl>(
    withFilterDefaults(initFilterControl)
  );
  const [activeFilter, setActiveFilter] = useState<FilterType | null>(null);

  // EIS-1097 round 4: the collapsed row's summary in TWO pieces, because the
  // marker must not be renderable inside the node that ellipsizes. See the row.
  const previousSalesRow = previousSalesSummaryParts(filterControl?.previousSales);

  const resetFilters = () => {
    onApplyFilter(DEFAULT_VALUES);
    setFilterControl(DEFAULT_VALUES);
  }

  return (
    <Box pos="relative" h="90%">
      <FilterModal
        filterControl={filterControl}
        setFilterControl={setFilterControl}
        activeFilter={activeFilter}
        onApplyFilter={(f) => {
          if (f) {
            onApplyFilter(f);
          }
          setActiveFilter(null);
        }}
        onClose={() => {
          setActiveFilter(null);
        }}
      />
      <Text fz="sm" my="sm">
        Show <b>only pins</b> on map by:
      </Text>
      <Stack justify="space-between"
        style={{
          boxSizing: "border-box",
          height: "100%",
        }}>
        <Stack gap={0} ml="15px">
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            onClick={() => setActiveFilter("last-check-in")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("last-check-in")}
            </Text>
            <Box>
              {colorControl === "last-check-in" ? (
                <PalleteIcon width={24} />
              ) : null}
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            onClick={() => setActiveFilter("status")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("status")}
            </Text>
            <Box>
              {colorControl === "status" ? (
                <PalleteIcon width={24} />
              ) : null}
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            onClick={() => setActiveFilter("specialty-type")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("specialty-type")}
            </Text>
            <Box>
              {colorControl === "specialty-type" ? (
                <PalleteIcon width={24} />
              ) : null}
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
          {/* EIS-1088: Source. There is no ColorType for it, so no palette icon. */}
          <Group
            justify="space-between"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            data-testid="map-filter-row-source"
            onClick={() => setActiveFilter("source")}>
            <Text
              fw="bold"
              lh="40px">
              {TYPE_TITLE_MAP.get("source")}
            </Text>
            <Box>
              <RightIcon width={24} style={{ marginBottom: "2px" }} />
            </Box>
          </Group>
          {/* EIS-1088: Previous Sales. No ColorType either.
              EIS-1097: this is now the ONLY sales row -- the standalone Rolling
              12-Month Sales row that used to sit below it is deleted, and its
              dollar range lives in this row's "Has" segment. The collapsed row
              carries a summary because with three revealed controls behind it,
              a bare chevron would no longer say what the filter is doing. */}
          <Group
            justify="space-between"
            wrap="nowrap"
            h={40}
            style={{
              borderBottom: "1px solid #F0F0F0",
            }}
            data-testid="map-filter-row-previous-sales"
            onClick={() => setActiveFilter("previous-sales")}>
            <Text
              fw="bold"
              lh="40px"
              style={{ whiteSpace: "nowrap" }}>
              {TYPE_TITLE_MAP.get("previous-sales")}
            </Text>
            <Group gap={4} wrap="nowrap" style={{ minWidth: 0 }}>
              {/* Derived from the SAME union buildMapFilterBody reads, so the
                  row can never describe a filter the server was not asked for.

                  ROUND 4 -- why this is two elements and not one string. The
                  marker used to be the LAST word of the truncated <Text>, so on
                  a narrow panel the ellipsis ate exactly the words that explain
                  the empty map ("...last 12 months · no matches possib..."). The
                  marker is the load-bearing half of the round-3 decision to warn
                  rather than block, and it was the first thing CSS removed.
                  So: the marker renders FIRST and cannot shrink (flexShrink 0 +
                  nowrap), and only the describable detail truncates. This
                  wrapper keeps the row's existing testid and every word the row
                  says, while `wrap="nowrap"` + lh 40px keep it one 40px line.
                  Reading the wrapper's raw textContent in the impossible case
                  gets the marker before the detail, which is why round 5 added
                  the canonical sentence below as its own node. */}
              <Group
                gap={6}
                wrap="nowrap"
                style={{ minWidth: 0 }}
                data-testid="map-filter-row-previous-sales-summary">
                {previousSalesRow.noMatches ? (
                  <>
                    {/* ROUND 5 -- the row's ACCESSIBLE copy of the same sentence.
                        Putting the marker first (above) is right for the eye, but
                        it left a screen reader announcing the two visible nodes in
                        DOM order and with no separator:
                          "no matches possibleHas previous sales · $5,000–$100 · ..."
                        -- reordered, and run together. So the canonical joined
                        string (exactly what previousSalesSummary() returns) is
                        rendered once, hidden from the eye but not from the
                        accessibility tree, and the two VISIBLE fragments are
                        hidden from that tree instead, so it is announced once, in
                        the right order. Nothing about the visible layout moves:
                        VisuallyHidden is position:absolute, so it is out of the
                        flex flow and the row stays one 40px line.

                        Why a hidden text node and NOT aria-label on the wrapper:
                        the wrapper is a plain <div> (ARIA role `generic`), and
                        ARIA 1.2 prohibits naming a generic element -- browsers and
                        AT are free to drop that label, which would make the fix a
                        no-op exactly where it is needed. Giving the wrapper an
                        invented role (`img` announces text as an image, `text` is
                        Safari-only) to make the label legal costs more than it
                        buys. A real text node needs no role and no exception. */}
                    <VisuallyHidden data-testid="map-filter-row-previous-sales-full-text">
                      {previousSalesRow.full}
                    </VisuallyHidden>
                    <Text
                      fz="xs"
                      fw={600}
                      c="#B54708"
                      lh="40px"
                      aria-hidden
                      style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                      data-testid="map-filter-row-previous-sales-no-matches">
                      {PREVIOUS_SALES_NO_MATCHES_SUMMARY}
                    </Text>
                  </>
                ) : null}
                <Text
                  fz="xs"
                  c="#6B6B6B"
                  lh="40px"
                  truncate
                  {...(previousSalesRow.noMatches ? { "aria-hidden": true } : {})}
                  data-testid="map-filter-row-previous-sales-summary-detail">
                  {previousSalesRow.detail}
                </Text>
              </Group>
              <Box>
                <RightIcon width={24} style={{ marginBottom: "2px" }} />
              </Box>
            </Group>
          </Group>
        </Stack>
        <Button
          bg="#FF9D00"
          fullWidth
          style={{ boxSizing: "border-box" }}
          onClick={() => {
            resetFilters();
          }}>
          Reset Filters
        </Button>
      </Stack>
    </Box>
  );
};

export default FiltersPanel;